/**
 * Report form (attendance).
 */
import * as React from 'react';
import { Users } from 'lucide-react';
import { AttendanceRow } from '@/components/common/AttendanceRow';
import { ArchivedAttendanceRow } from '@/components/common/ArchivedAttendanceRow';
import type { ParticipantAttendance } from '@/types/attendance.types';
import type { SessionParticipant } from '@/types/inperson/session.types';
import {
  attendanceValueFor,
  INPERSON_REPORT_OFFERED_ATTENDANCE_STATUSES,
  isArchivedParticipant,
  sortAttendanceParticipants,
} from '@/components/reports/inperson/report-form-utils';

/** Props for {@link AttendanceSection}. */
interface AttendanceSectionProps {
  participants: SessionParticipant[];
  attendanceData: Map<string, ParticipantAttendance>;
  errors: Map<string, string>;
  onChange: (participantId: string, next: ParticipantAttendance) => void;
}

/** Attendance rows for the in-person report form. */
export const AttendanceSection: React.FC<Readonly<AttendanceSectionProps>> = ({
  participants,
  attendanceData,
  errors,
  onChange,
}) => {
  if (participants.length === 0) return null;

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Users className="w-4 h-4" />
        Présences
      </h4>
      <div className="rounded-lg border border-border bg-muted/25 p-4 space-y-3">
        {sortAttendanceParticipants(participants).map((participant) => {
          if (isArchivedParticipant(participant)) {
            return <ArchivedAttendanceRow key={participant.id} participant={participant} />;
          }
          return (
            <AttendanceRow
              key={participant.id}
              participant={participant}
              value={attendanceValueFor(participant, attendanceData)}
              error={errors.get(participant.id)}
              offeredStatuses={INPERSON_REPORT_OFFERED_ATTENDANCE_STATUSES}
              onChange={(next) => onChange(participant.id, next)}
            />
          );
        })}
      </div>
    </div>
  );
};
